import type { PackingState } from '../domain/schema';
import { mergeTrip } from './trip-merge';

export type SaveStatus = 'saved' | 'saving' | 'failed';

/** One immediate writer per editor; a late ACK rebases rather than replacing newer input. */
export class TripSaveSession {
  private base: PackingState;
  private version = 0;
  private running = false;
  private waiters: Array<{ version: number; resolve(value: boolean): void }> = [];
  state: PackingState;
  status: SaveStatus = 'saved';
  error?: Error;

  constructor(initial: PackingState, private write: (next: PackingState, base: PackingState) => Promise<PackingState>, private notify: () => void) {
    this.base = initial; this.state = initial;
  }

  change(next: PackingState): Promise<boolean> {
    this.state = next; this.version++;
    const result = new Promise<boolean>((resolve) => this.waiters.push({ version: this.version, resolve }));
    void this.pump();
    return result;
  }

  retry() { return this.change(this.state); }

  private async pump() {
    if (this.running) { this.notify(); return; }
    this.running = true; this.status = 'saving'; this.error = undefined; this.notify();
    try {
      while (true) {
        const version = this.version, sent = this.state;
        const saved = await this.write(sent, this.base);
        const rebased = version === this.version ? saved : mergeTrip(sent, this.state, saved);
        // A failed rebase must retain the last observed base for every retry.
        this.base = saved; this.state = rebased;
        const resolved = this.waiters.filter((waiter) => waiter.version <= version);
        this.waiters = this.waiters.filter((waiter) => waiter.version > version);
        for (const waiter of resolved) waiter.resolve(true);
        if (version === this.version) break;
      }
      this.status = 'saved';
    } catch (error) {
      this.status = 'failed'; this.error = error instanceof Error ? error : new Error('기기에 저장하지 못했어요.');
      for (const waiter of this.waiters) waiter.resolve(false);
      this.waiters = [];
    } finally { this.running = false; this.notify(); }
  }
}
